import type { Query, QueryClient } from '@tanstack/react-query';
import { BULK_MOVE_MAX_TICKETS, type BulkMoveResult } from '@vd/types';
import { BULK_REQUEST_LIMIT, settleWithLimit } from '../../lib/settleWithLimit';
import { keyStartsWith, projectTicketListsKey, projectTicketSearchesKey } from './useBoardData';

/**
 * Moving a selection of tickets, and saying truthfully what happened
 * (TOADO-976).
 *
 * Each move ends in one of six outcomes, decided only by what the server
 * answered for it:
 *
 * - moved: it answered success;
 * - refused: it answered with a 4xx, which it sends before storing anything,
 *   so the ticket did not move and moving it again is safe;
 * - signedOut: it answered 401. Not moved; the person has already been sent
 *   to sign in (`vd:unauthorized`), and can move it once they have;
 * - forbidden: it answered 403. Not moved, and trying again would only be
 *   refused again: they may not move it;
 * - unconfirmed: no answer, or a 5xx. The move may well have been stored (the
 *   answer can be lost after the commit), and nothing the browser can read
 *   afterwards settles it: a read can come before a slow commit, or after a
 *   teammate moved the ticket back. So these are not offered for another
 *   try; the refreshed board shows where they are;
 * - notYet: not moved because the moves a minute ran out (a 429), or the bulk
 *   route was unavailable for a moment (`bulk_move_unavailable`), and those
 *   after them were not sent. Safe to move again, after a wait, which
 *   `notYetBecause` says (review round 4: told only "refused", the person
 *   tried again at once and was refused again).
 */
export interface BulkMoveOutcome {
  moved: string[];
  refused: string[];
  signedOut: string[];
  forbidden: string[];
  unconfirmed: string[];
  notYet: string[];
  notYetBecause?: 'limit' | 'unavailable';
}

/**
 * The two ways to ask: `/tickets/bulk-move` for many, and `/tickets/:id/move`
 * for one, which a server without the bulk route still has.
 */
export interface MoveRequests {
  many: (ticketIds: string[]) => Promise<BulkMoveResult[]>;
  one: (ticketId: string) => Promise<unknown>;
}

/**
 * Tickets in one bulk request: half what the server takes, so one request's
 * work stays short.
 */
export const BULK_MOVE_CHUNK = Math.min(50, BULK_MOVE_MAX_TICKETS);

/**
 * Move the tickets in requests of `chunk`, one request after another, so the
 * moves land in the order given and a selection costs a write per request
 * rather than per ticket: a request per ticket ran a move of 238 into the
 * server's 200 writes a minute (TOADO-976).
 *
 * A request answered as a whole (no answer, a 5xx, a 401, a refusal) gives
 * each of its tickets that outcome; the bulk route saying it is unavailable
 * (Redis away) moved nothing, so its tickets are not moved yet, not
 * unconfirmed. After a 401, or a 429 or that unavailability, for a whole
 * request or one ticket's, nothing more is sent, and what was not sent is not
 * moved: each would only be refused the same way (review rounds 2 and 4).
 *
 * A 404 for the whole request is a server without the bulk route (the bulk
 * route answers a missing ticket in its results, never so), met during a
 * deploy while an old API still answers: that request's tickets and the rest
 * go one request each, as before.
 */
export async function moveTickets(
  ticketIds: readonly string[],
  requests: MoveRequests,
  chunk = BULK_MOVE_CHUNK,
): Promise<BulkMoveOutcome> {
  const outcome: BulkMoveOutcome = { moved: [], refused: [], signedOut: [], forbidden: [], unconfirmed: [], notYet: [] };
  /** Why nothing more is sent, once an answer says so: the bucket the rest go in. */
  let stop: 'signedOut' | 'notYet' | undefined;
  /** Not moved yet, for this answer's reason; the first reason is the one the toast gives. */
  const waitFor = (error: unknown): BulkMoveOutcome['notYetBecause'] => {
    if (statusOf(error) === 429) return 'limit';
    return codeOf(error) === 'bulk_move_unavailable' ? 'unavailable' : undefined;
  };
  /**
   * Note that an answer stops what is not yet sent, as soon as it comes back.
   * A refused sign-in outranks a wait: unsent tickets would only be refused
   * again until the person signs in (review round 5).
   */
  const heed = (error: unknown) => {
    if (statusOf(error) === 401) stop = 'signedOut';
    else if (!stop && waitFor(error)) {
      stop = 'notYet';
      outcome.notYetBecause ??= waitFor(error);
    }
  };
  /** One ticket, by its answer. */
  const sort = (id: string, error: unknown) => {
    heed(error);
    const status = statusOf(error);
    const because = waitFor(error);
    if (because) {
      outcome.notYet.push(id);
      outcome.notYetBecause ??= because;
    } else if (status === 401) outcome.signedOut.push(id);
    else if (status === 403) outcome.forbidden.push(id);
    else if (typeof status === 'number' && status >= 400 && status < 500) outcome.refused.push(id);
    else outcome.unconfirmed.push(id);
  };
  let bulk = true;
  for (let start = 0; start < ticketIds.length; start += chunk) {
    const ids = ticketIds.slice(start, start + chunk);
    if (stop) {
      outcome[stop].push(...ids);
      continue;
    }
    if (bulk) {
      try {
        const results = new Map((await requests.many(ids)).map((result) => [result.ticketId, result]));
        for (const id of ids) {
          const result = results.get(id);
          if (result?.moved) outcome.moved.push(id);
          // Asked for and not answered for: as good as no answer.
          else sort(id, result ? { status: result.status, code: result.code } : undefined);
        }
        continue;
      } catch (error) {
        if (statusOf(error) !== 404) {
          for (const id of ids) sort(id, error);
          continue;
        }
        bulk = false;
      }
    }
    // A ticket at a time, at most BULK_REQUEST_LIMIT at once: none is started
    // once an answer has said to stop, and those not started were not moved.
    const results = await settleWithLimit(ids, BULK_REQUEST_LIMIT, async (id) => {
      if (stop) throw NOT_SENT;
      try {
        return await requests.one(id);
      } catch (error) {
        heed(error);
        throw error;
      }
    });
    ids.forEach((id, index) => {
      const result = results[index]!;
      if (result.status === 'fulfilled') outcome.moved.push(id);
      else if (result.reason === NOT_SENT) outcome[stop!].push(id);
      else sort(id, result.reason);
    });
  }
  return outcome;
}

/** A move not sent because an earlier answer said to stop: it goes where that answer's tickets went. */
const NOT_SENT = Symbol('not sent');

const statusOf = (error: unknown) => (error as { status?: unknown } | null)?.status;
const codeOf = (error: unknown) => (error as { code?: unknown } | null)?.code;

/**
 * The toasts saying a project's board did not refresh.
 *
 * By project, because a refresh reloads only the projects it names: one that
 * lands for project A says nothing about B, so a toast about A and B is
 * answered only by a refresh that reloaded both. Held here rather than in the
 * board page, because toasts outlive the page: leave the board and come back,
 * and the toast from before is still up, still pressable.
 */
const staleToasts = new Map<string, ReadonlySet<string>>();

export const staleBoards = {
  /** A toast saying these projects' boards did not refresh. */
  add(toastId: string, projectIds: readonly string[]): void {
    staleToasts.set(toastId, new Set(projectIds));
  },
  /** A toast that has gone, however it went. */
  forget(toastId: string): void {
    staleToasts.delete(toastId);
  },
  /** The toasts a refresh of these projects answers: those whose every project it reloaded. */
  answeredBy(projectIds: readonly string[]): string[] {
    const reloaded = new Set(projectIds);
    return [...staleToasts].filter(([, projects]) => [...projects].every((project) => reloaded.has(project))).map(([id]) => id);
  },
  /** Whether this toast still says its board is stale: no refresh has answered it since. */
  isStale(toastId: string): boolean {
    return staleToasts.has(toastId);
  },
  /** For tests: the module's state goes back to empty. (Refresh queues are per client, so a new client starts empty.) */
  reset(): void {
    staleToasts.clear();
  },
};

/** How long a refresh may take before it is reported as not done; it carries on regardless. */
export const REFRESH_WAIT_MS = 20_000;

/**
 * Refetch what the board shows for these projects, and say whether each of
 * those lists really came back from the server.
 *
 * "What the board shows" is every ticket list of the projects, and the search
 * results while a search is on. Those not on screen are dropped, so they load
 * afresh when next shown: only marking them stale was undone by the first
 * live event written into them.
 *
 * "Came back from the server" is a fetch's own success, heard from the query
 * cache, not any later timestamp: a live event or an optimistic write also
 * updates a list, a fetch can be cancelled and the old data put back, and an
 * offline fetch pauses, at its start or partway through, and would hold the
 * answer forever. Whatever has not fetched successfully within
 * REFRESH_WAIT_MS counts as not refreshed.
 */
export function refreshTicketLists(qc: QueryClient, projectIds: readonly string[], waitMs = REFRESH_WAIT_MS): Promise<boolean> {
  // One refresh at a time, from a bulk move or any toast's Refresh: two at
  // once would refetch the same lists, one cancelling the other's request,
  // and each would then report on data the other fetched.
  //
  // A refresh of the same projects already waiting its turn is shared rather
  // than queued again: it will fetch after everything before it, which is all
  // a second one could do, and a slow backend then costs one fetch, not one
  // per press. Both are per query client (the lists are that client's).
  //
  // Each caller's wait counts from when it asked, the time in the queue
  // included, and bounds only its own answer: a refresh asked for during an
  // outage answers within its wait however many are ahead of it. A caller
  // that shares a waiting run moves the run's own deadline to the later of
  // the two, so a retry after an earlier caller's time ran out still fetches;
  // a run that starts after every sharer's time has run out fetches nothing.
  const deadline = Date.now() + waitMs;
  const queue = queueFor(qc);
  // JSON, so no project id can run into the next one.
  const key = JSON.stringify([...new Set(projectIds)].sort());
  const queued = queue.waiting.get(key);
  if (queued) {
    queued.deadline = Math.max(queued.deadline, deadline);
    return byDeadline(queued.run, deadline);
  }
  // Assigned below, before the queue can reach the run that reads it.
  let entry!: WaitingRefresh;
  const run = queue.last.then(() => {
    // Still this run's entry: callers that came while it waited shared it, and
    // only from here can another be queued under the key.
    queue.waiting.delete(key);
    if (Date.now() >= entry.deadline) return false;
    return refreshNow(qc, projectIds, entry.deadline);
  });
  entry = { deadline, run };
  queue.last = run.catch(() => undefined);
  queue.waiting.set(key, entry);
  return byDeadline(run, deadline);
}

/** The run's answer, or not done once the deadline passes, whichever comes first. */
function byDeadline(run: Promise<boolean>, deadline: number): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    run,
    new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(false), Math.max(0, deadline - Date.now())); }),
  ]).finally(() => clearTimeout(timer));
}

/** A refresh queued and not yet started: the latest deadline of the callers sharing it, and its run. */
interface WaitingRefresh {
  deadline: number;
  run: Promise<boolean>;
}
interface RefreshQueue {
  last: Promise<unknown>;
  waiting: Map<string, WaitingRefresh>;
}
const queues = new WeakMap<QueryClient, RefreshQueue>();
function queueFor(qc: QueryClient): RefreshQueue {
  let queue = queues.get(qc);
  if (!queue) {
    queue = { last: Promise.resolve(), waiting: new Map() };
    queues.set(qc, queue);
  }
  return queue;
}

async function refreshNow(qc: QueryClient, projectIds: readonly string[], deadline: number): Promise<boolean> {
  // The whole refresh within the caller's deadline, the cancellation before
  // the fetches included: one that never settled would hold up every refresh
  // queued behind it.
  const projects = new Set(projectIds);
  // By the keys the lists are made with, so a renamed or reordered key is
  // renamed here too rather than silently left unrefreshed.
  const shows = (query: Query) => [...projects].some((project) => keyStartsWith(query.queryKey, projectTicketListsKey(project))
    || keyStartsWith(query.queryKey, projectTicketSearchesKey(project)));
  const cache = qc.getQueryCache();
  for (const query of cache.findAll({ predicate: (q) => shows(q) && !q.isActive() })) cache.remove(query);
  const onScreen = cache.findAll({ predicate: (q) => shows(q) && q.isActive() });
  if (onScreen.length === 0) return true;

  // A fetch already in flight may have left before the moves were stored, and
  // fetching again would only join it: cancelled first, so what counts is a
  // request made after the moves.
  // A cancellation that fails still lets the fetches go ahead (each asks to
  // cancel again with cancelRefetch); one that has not settled by the
  // deadline ends the refresh as not done.
  let cancelTimer: ReturnType<typeof setTimeout> | undefined;
  const settledInTime = await Promise.race([
    qc.cancelQueries({ predicate: (query) => onScreen.includes(query) }).then(() => true, () => true),
    new Promise<boolean>((resolve) => { cancelTimer = setTimeout(() => resolve(false), Math.max(0, deadline - Date.now())); }),
  ]);
  clearTimeout(cancelTimer);
  if (!settledInTime) return false;

  const fetched = new Set<Query>();
  let settle: () => void = () => {};
  const allFetched = new Promise<void>((resolve) => { settle = resolve; });
  const unsubscribe = cache.subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success' || event.action.manual) return;
    if (!onScreen.includes(event.query)) return;
    fetched.add(event.query);
    if (fetched.size === onScreen.length) settle();
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const attempts = Promise.allSettled(onScreen.map((query) => query.fetch(undefined, { cancelRefetch: true })));
    await Promise.race([
      allFetched,
      attempts.then(() => undefined),
      new Promise<void>((resolve) => { timer = setTimeout(resolve, Math.max(0, deadline - Date.now())); }),
    ]);
  } finally {
    clearTimeout(timer);
    unsubscribe();
  }
  return onScreen.every((query) => fetched.has(query));
}
